/*
 * Scroll gallery
 * ------------------------------------------------------------
 * A looping horizontal scroller where the photo closest to the centre is the
 * "focused" one.
 *
 *  1. Loop. Liquid prints the strip once; this file clones it until the copies
 *     cover the screen plus one strip on each side, then wraps `scrollLeft` by
 *     exactly one strip width whenever it leaves the middle window. The copies
 *     are identical, so the jump is invisible and there is never an empty
 *     margin.
 *
 *  2. Focus. The photo nearest the horizontal centre gets `--sg-focus: 1`,
 *     fading to 0 over `focus_range`. It buys two things and nothing else: the
 *     `z-index` written here (so the centred photo paints in front of its
 *     neighbours) and the small scale bump the CSS applies. Rotation and size
 *     stay exactly as the merchant set them, so the composition never reflows —
 *     unwinding the tilt is opt-in via `focus_straighten`.
 *
 *     Geometry is measured once per layout change into `this.slots`, never from
 *     getBoundingClientRect: the elements are transformed by this very effect,
 *     so their painted boxes would feed back into the next frame.
 *
 *  3. Auto-advance. While the section crosses the viewport the page scroll also
 *     drives `scrollLeft`, pivoting on the resting position so halfway through
 *     the crossing the composition is the design's. It stops for good the moment
 *     the visitor takes over — the two would otherwise fight over `scrollLeft`.
 *
 * Dragging is wired by hand (a mouse without a trackpad has no horizontal
 * wheel); wheel, touch and keyboard scrolling are the browser's own.
 */
(function () {
  'use strict';

  var instances = new WeakMap();
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function ScrollGallery(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-scroll-gallery-viewport]');
    this.track = root.querySelector('[data-scroll-gallery-track]');
    this.source = root.querySelector('[data-scroll-gallery-source]');
    if (!this.viewport || !this.track || !this.source) return;

    this.travel = (parseFloat(root.dataset.travel) || 0) / 100;
    this.focusRange = (parseFloat(root.dataset.focusRange) || 30) / 100;
    this.autoScroll = root.dataset.autoScroll !== 'false';
    this.sign = root.dataset.direction === 'right' ? -1 : 1;

    this.visible = false;
    this.ticking = false;
    this.taken = false; // the visitor has taken over the strip
    this.drag = null;
    this.slots = [];
    this.period = 0;
    this.base = 0;
    this.rest = 0;
    this.width = 0;

    this.onFrame = this.onFrame.bind(this);
    this.request = this.request.bind(this);
    this.onTakeOver = this.onTakeOver.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onDragStart = this.onDragStart.bind(this);

    this.observer = new IntersectionObserver(
      function (entries) {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.request();
      }.bind(this),
      { rootMargin: '10% 0px' }
    );
    this.observer.observe(root);

    window.addEventListener('scroll', this.request, { passive: true });
    window.addEventListener('resize', this.request);
    this.viewport.addEventListener('scroll', this.request, { passive: true });

    this.viewport.addEventListener('wheel', this.onTakeOver, { passive: true });
    this.viewport.addEventListener('touchstart', this.onTakeOver, { passive: true });
    this.viewport.addEventListener('keydown', this.onTakeOver);
    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    // Belt and braces with `-webkit-user-drag: none`: without this, a drag that
    // starts on a photo becomes the browser's own image drag and the swipe dies.
    this.viewport.addEventListener('dragstart', this.onDragStart);

    this.layout();
    this.request();
  }

  /* ---------------------------------------------------------------- layout */

  /* Enough copies to cover the screen plus a whole strip on either side, so
     `scrollLeft` can always be wrapped back into the middle copy. */
  ScrollGallery.prototype.copiesNeeded = function () {
    if (this.period <= 0) return 1;
    return Math.ceil(this.width / this.period) + 2;
  };

  ScrollGallery.prototype.layout = function () {
    this.width = this.viewport.clientWidth;
    this.period = this.source.offsetWidth;
    if (!this.period || !this.width) return;

    var wanted = this.copiesNeeded();
    var copies = this.track.querySelectorAll('.scroll-gallery__strip');
    var i;

    for (i = copies.length; i < wanted; i++) {
      var clone = this.source.cloneNode(true);
      clone.removeAttribute('data-scroll-gallery-source');
      clone.setAttribute('aria-hidden', 'true');
      // The editor identifies blocks by these attributes; duplicates would make
      // it select the wrong one.
      Array.prototype.forEach.call(clone.querySelectorAll('[data-shopify-editor-block]'), function (el) {
        el.removeAttribute('data-shopify-editor-block');
      });
      this.track.appendChild(clone);
    }
    for (i = copies.length - 1; i >= wanted; i--) {
      copies[i].remove();
    }

    // Wrap window: one strip wide, centred in the scrollable range so there is
    // the same amount of slack on both sides. Anchoring it to the first copy
    // instead would leave almost none at the far end, and a fast fling would
    // hit the scroller's hard edge before the wrap could run.
    var max = this.track.scrollWidth - this.width;
    this.base = Math.max(0, (max - this.period) / 2);

    this.measure();
    this.centreOnMiddlePhoto();
  };

  /* One pass of layout reads, cached until the next resize. */
  ScrollGallery.prototype.measure = function () {
    var slots = [];
    Array.prototype.forEach.call(this.track.querySelectorAll('.scroll-gallery__strip'), function (strip) {
      var base = strip.offsetLeft;
      Array.prototype.forEach.call(strip.querySelectorAll('.scroll-gallery__item'), function (item) {
        slots.push({ el: item, centre: base + item.offsetLeft + item.offsetWidth / 2 });
      });
    });
    this.slots = slots;
  };

  /* Start on the middle photo of the second copy, which is the composition the
     design file shows. */
  ScrollGallery.prototype.centreOnMiddlePhoto = function () {
    var strips = this.track.querySelectorAll('.scroll-gallery__strip');
    if (!strips.length) return;

    var strip = strips[Math.min(1, strips.length - 1)];
    var items = Array.prototype.slice.call(strip.querySelectorAll('.scroll-gallery__item'));
    if (!items.length) return;

    items.sort(function (a, b) {
      return a.offsetLeft + a.offsetWidth / 2 - (b.offsetLeft + b.offsetWidth / 2);
    });
    var middle = items[Math.floor((items.length - 1) / 2)];
    var centre = strip.offsetLeft + middle.offsetLeft + middle.offsetWidth / 2;

    this.rest = this.wrapValue(centre - this.width / 2);
    this.viewport.scrollLeft = this.rest;
  };

  /* ------------------------------------------------------------------ loop */

  ScrollGallery.prototype.wrapValue = function (value) {
    if (this.period <= 0) return value;
    var offset = (value - this.base) % this.period;
    if (offset < 0) offset += this.period;
    return this.base + offset;
  };

  /* Returns the shift applied, so a drag in progress can keep its reference. */
  ScrollGallery.prototype.wrapScroll = function () {
    if (this.period <= 0) return 0;
    var current = this.viewport.scrollLeft;
    var shift = 0;
    if (current < this.base) shift = this.period;
    else if (current >= this.base + this.period) shift = -this.period;
    if (shift) this.viewport.scrollLeft = current + shift;
    return shift;
  };

  /* ----------------------------------------------------------- interaction */

  ScrollGallery.prototype.onTakeOver = function () {
    this.taken = true;
  };

  ScrollGallery.prototype.onDragStart = function (event) {
    event.preventDefault();
  };

  ScrollGallery.prototype.onPointerDown = function (event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.taken = true;
    this.drag = { x: event.clientX, scroll: this.viewport.scrollLeft, moved: false, id: event.pointerId };
    this.viewport.addEventListener('pointermove', this.onPointerMove);
    this.viewport.addEventListener('pointerup', this.onPointerUp);
    this.viewport.addEventListener('pointercancel', this.onPointerUp);
  };

  ScrollGallery.prototype.onPointerMove = function (event) {
    if (!this.drag) return;
    var dx = event.clientX - this.drag.x;
    if (!this.drag.moved && Math.abs(dx) > 3) {
      this.drag.moved = true;
      this.viewport.classList.add('is-dragging');
      // Capture only once it is clearly a drag, so a plain click still works.
      if (this.viewport.setPointerCapture) this.viewport.setPointerCapture(this.drag.id);
    }
    if (!this.drag.moved) return;
    event.preventDefault();

    this.viewport.scrollLeft = this.drag.scroll - dx;
    // Wrapping mid-drag moves the ground under the pointer; shift the drag's
    // own origin by the same amount so the photos keep following the cursor.
    this.drag.scroll += this.wrapScroll();
  };

  ScrollGallery.prototype.onPointerUp = function () {
    if (this.drag && this.drag.moved && this.viewport.releasePointerCapture) {
      try {
        this.viewport.releasePointerCapture(this.drag.id);
      } catch (e) {
        /* the pointer was already released */
      }
    }
    this.drag = null;
    this.viewport.classList.remove('is-dragging');
    this.viewport.removeEventListener('pointermove', this.onPointerMove);
    this.viewport.removeEventListener('pointerup', this.onPointerUp);
    this.viewport.removeEventListener('pointercancel', this.onPointerUp);
  };

  /* ----------------------------------------------------------------- frame */

  ScrollGallery.prototype.request = function () {
    if (this.ticking) return;
    this.ticking = true;
    window.requestAnimationFrame(this.onFrame);
  };

  ScrollGallery.prototype.onFrame = function () {
    this.ticking = false;
    if (!this.visible) return;

    if (this.width !== this.viewport.clientWidth) {
      var taken = this.taken;
      this.layout(); // re-centres, which is right on a resize
      this.taken = taken;
    }

    if (this.autoScroll && !this.taken && !reduceMotion.matches) this.advance();
    else if (!this.drag) this.wrapScroll();

    this.paint();
  };

  ScrollGallery.prototype.advance = function () {
    var rect = this.root.getBoundingClientRect();
    var span = window.innerHeight + rect.height;
    if (span <= 0 || this.period <= 0) return;

    var progress = (window.innerHeight - rect.top) / span;
    progress = Math.min(1, Math.max(0, progress));

    var target = this.rest + (progress - 0.5) * this.period * this.travel * this.sign;
    this.viewport.scrollLeft = this.wrapValue(target);
  };

  ScrollGallery.prototype.paint = function () {
    if (!this.width) return;

    var centre = this.viewport.scrollLeft + this.width / 2;
    var range = this.width * this.focusRange;

    this.slots.forEach(function (slot) {
      var focus = 0;
      if (range > 0) {
        focus = Math.max(0, 1 - Math.abs(slot.centre - centre) / range);
        // Smoothstep, so a photo eases into the front instead of ramping.
        focus = focus * focus * (3 - 2 * focus);
      }
      slot.el.style.setProperty('--sg-focus', focus.toFixed(4));
      slot.el.style.zIndex = String(Math.round(focus * 100));
    });
  };

  ScrollGallery.prototype.destroy = function () {
    window.removeEventListener('scroll', this.request);
    window.removeEventListener('resize', this.request);
    if (this.viewport) {
      this.viewport.removeEventListener('scroll', this.request);
      this.viewport.removeEventListener('wheel', this.onTakeOver);
      this.viewport.removeEventListener('touchstart', this.onTakeOver);
      this.viewport.removeEventListener('keydown', this.onTakeOver);
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('dragstart', this.onDragStart);
      this.onPointerUp();
    }
    if (this.observer) this.observer.disconnect();
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-scroll-gallery]');
    Array.prototype.forEach.call(roots, function (root) {
      var existing = instances.get(root);
      if (existing) existing.destroy();
      instances.set(root, new ScrollGallery(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector('[data-scroll-gallery]');
    if (!root) return;
    var instance = instances.get(root);
    if (instance) {
      instance.destroy();
      instances.delete(root);
    }
  });
})();
